import React, { useState, useEffect } from 'react';
import api from '../api';
import { useToast } from '../Toast';

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [groupMembers, setGroupMembers] = useState({}); // { groupId: [userId, ...] }
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null); // null = new
  const [form, setForm] = useState({ name: '', member_ids: [] });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const gr = await api.get('/procedures/groups');
      setGroups(gr.data);
      const map = {};
      gr.data.forEach(g => { map[g.id] = g.member_ids || []; });
      setGroupMembers(map);
    } catch (e) { toast('Błąd grup: ' + (e.response?.status || e.message), 'error'); }
    try {
      const us = await api.get('/auth/users');
      setUsers((us.data || []).filter(u => u.active !== 0));
    } catch (e) { toast('Błąd użytkowników: ' + (e.response?.status || e.message), 'error'); }
  }

  function openNew() {
    setEditItem(null);
    setForm({ name: '', member_ids: [] });
    setShowModal(true);
  }

  async function openEdit(g) {
    // members are stored in groupMembers state loaded at startup
    const member_ids = groupMembers[g.id] || [];
    setEditItem(g);
    setForm({ name: g.name, member_ids });
    setShowModal(true);
  }

  function toggleMember(uid) {
    setForm(f => ({
      ...f,
      member_ids: f.member_ids.includes(uid)
        ? f.member_ids.filter(x => x !== uid)
        : [...f.member_ids, uid],
    }));
  }

  async function save(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast('Podaj nazwę grupy', 'error'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/procedures/groups/${editItem.id}`, form);
        toast('Grupa zaktualizowana');
      } else {
        await api.post('/procedures/groups', form);
        toast('Grupa dodana');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'Błąd zapisu', 'error');
    } finally { setSaving(false); }
  }

  async function del(g) {
    if (!window.confirm(`Usunąć grupę "${g.name}"? Zostanie usunięta również z procedur.`)) return;
    try {
      await api.delete(`/procedures/groups/${g.id}`);
      toast('Usunięto');
      load();
    } catch (err) { toast(err.response?.data?.error || 'Błąd', 'error'); }
  }

  const chipStyle = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '5px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
    border: `1px solid ${active ? '#c53030' : 'var(--gray-200)'}`,
    background: active ? '#fff5f5' : 'var(--gray-50)',
    color: active ? '#c53030' : 'var(--gray-600)',
    fontWeight: active ? 600 : 400,
    userSelect: 'none', transition: 'all .15s',
  });

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '28px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Grupy pracowników</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: 14 }}>
            Twórz grupy (np. Sprzedawcy, Konstruktorzy) i przypisuj je jako Wykonujący w procedurach.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Nowa grupa</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {groups.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--gray-400)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
            Brak grup — dodaj pierwszą grupę klikając "+ Nowa grupa"
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-100)' }}>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase' }}>Nazwa grupy</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase' }}>Liczba osób</th>
                <th style={{ padding: '10px 16px', minWidth: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g, i) => (
                <tr key={g.id} style={{ borderBottom: i < groups.length - 1 ? '1px solid var(--gray-100)' : 'none' }}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: '#fee2e2', color: '#c53030',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 700, flexShrink: 0
                      }}>
                        {g.name.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{g.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      background: '#f1f5f9', color: '#475569'
                    }}>
                      👤 {g.member_count} {g.member_count === 1 ? 'osoba' : g.member_count < 5 ? 'osoby' : 'osób'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(g)}>Edytuj</button>
                      <button className="btn btn-sm" onClick={() => del(g)}
                        style={{ background: '#fee2e2', color: '#dc2626', border: 'none' }}>Usuń</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: '100%', maxWidth: 540, boxShadow: '0 20px 60px rgba(0,0,0,.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 17 }}>{editItem ? `Edytuj grupę: ${editItem.name}` : 'Nowa grupa'}</h3>
            <form onSubmit={save}>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Nazwa grupy *</label>
                <input
                  className="form-input"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="np. Sprzedawcy, Konstruktorzy, Lakiernicy…"
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: 24 }}>
                <label className="form-label">
                  Członkowie grupy
                  <span style={{ fontWeight: 400, color: 'var(--gray-400)', marginLeft: 6, fontSize: 12 }}>
                    ({form.member_ids.length} wybranych)
                  </span>
                </label>
                {users.length === 0 ? (
                  <p style={{ color: 'var(--gray-400)', fontSize: 13 }}>Brak użytkowników</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {users.map(u => (
                      <span key={u.id} style={chipStyle(form.member_ids.includes(u.id))} onClick={() => toggleMember(u.id)}>
                        {form.member_ids.includes(u.id) ? '✓ ' : ''}{u.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Anuluj</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Zapisuję...' : 'Zapisz'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
